import React, { useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { ShieldCheck, Info, Eye, FileText, CheckCircle2 } from "lucide-react";
import AOS from "aos";
import "aos/dist/aos.css";
import { useSEO } from "../Pages/Seo";

const AdvertisingDisclosure = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
    AOS.init({ duration: 500, once: true });
  }, []);

  useSEO({
    title: "Advertising Disclosure | Transparency & Ad Policy | Game Zone Hub",
    description: "Read our Advertising Disclosure to understand how advertising supports Game Zone Hub, our partnership with Google AdSense, cookie practices, and editorial independence.",
    keywords: "advertising disclosure, ad policy, Google AdSense disclosure, cookies policy, Game Zone Hub transparency, free online games ads",
    canonical: "https://www.gamezonehub.in/advertising-disclosure",
  });

  const schemaData = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": "Advertising Disclosure - Game Zone Hub",
    "description": "Full transparency statement regarding advertising monetization, Google AdSense compliance, and user data choices at Game Zone Hub.",
    "url": "https://www.gamezonehub.in/advertising-disclosure",
    "publisher": {
      "@type": "Organization",
      "name": "Game Zone Hub"
    }
  };

  return (
    <div className="relative bg-[#0b1120] min-h-screen text-gray-300 sm:pt-[120px] pt-[100px] sm:pb-20 pb-11 px-4">
      <Helmet>
        {schemaData && (
          <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(schemaData) }} />
        )}
      </Helmet>

      <div className="container mx-auto max-w-5xl">
        <div className="text-center mb-12" data-aos="fade-down">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-blue-500/10 border border-blue-500/30 text-blue-400 text-sm font-semibold mb-4">
            <ShieldCheck size={18} /> Transparency & Safety Guarantee
          </div>
          <h1 className="text-3xl sm:text-5xl font-extrabold text-white mb-4">
            Advertising <span className="bg-clip-text text-transparent bg-gradient-to-r from-cyan-400 via-blue-400 to-purple-300">Disclosure</span>
          </h1>
          <p className="text-gray-400 max-w-2xl mx-auto text-base sm:text-lg leading-relaxed">
            At Game Zone Hub, transparency and user trust are our highest priorities. This disclosure explains how our platform is funded, how advertisements work, and our strict commitment to editorial independence.
          </p>
        </div>

        <div className="space-y-8" data-aos="fade-up">
          {/* Section 1 */}
          <div className="bg-[#111827] rounded-3xl p-6 sm:p-10 border border-white/5 shadow-xl">
            <div className="flex items-center gap-4 mb-4">
              <div className="p-3 bg-blue-500/10 rounded-2xl text-blue-400">
                <Info size={24} />
              </div>
              <h2 className="text-xl sm:text-2xl font-bold text-white">1. Why We Show Advertisements</h2>
            </div>
            <p className="text-gray-300 leading-relaxed text-sm sm:text-base mb-4">
              Game Zone Hub provides 100% free access to hundreds of high-quality HTML5 browser games without requiring user registration, paid subscriptions, or microtransactions. Hosting server infrastructure, licensing games, and maintaining page speed require significant continuous investment.
            </p>
            <p className="text-gray-300 leading-relaxed text-sm sm:text-base">
              To keep our catalog completely free for players around the globe, we display non-intrusive advertisements across our platform. Advertising revenue directly funds site operation, technical maintenance, security updates, and new game curation.
            </p>
          </div>

          {/* Section 2 */}
          <div className="bg-[#111827] rounded-3xl p-6 sm:p-10 border border-white/5 shadow-xl">
            <div className="flex items-center gap-4 mb-4">
              <div className="p-3 bg-cyan-500/10 rounded-2xl text-cyan-400">
                <Eye size={24} />
              </div>
              <h2 className="text-xl sm:text-2xl font-bold text-white">2. Our Ad Partners & Google AdSense Compliance</h2>
            </div>
            <p className="text-gray-300 leading-relaxed text-sm sm:text-base mb-4">
              We partner with reputable ad networks, including <strong>Google AdSense</strong>, to serve contextually relevant advertisements. AdSense uses automated systems to display ads based on general content context and anonymized user signals.
            </p>
            <ul className="list-disc pl-6 space-y-2 text-gray-400 text-sm sm:text-base marker:text-cyan-400">
              <li>AdSense cookies (such as the DART cookie) allow Google and its partners to serve ads based on your visit to Game Zone Hub and other sites across the Internet.</li>
              <li>You can opt out of personalized advertising by visiting <a href="https://adssettings.google.com" target="_blank" rel="noopener noreferrer" className="text-cyan-400 underline hover:text-cyan-300">Google Ads Settings</a>.</li>
              <li>Alternatively, you can opt out of third-party vendor cookies by visiting <a href="https://www.aboutads.info/choices/" target="_blank" rel="noopener noreferrer" className="text-cyan-400 underline hover:text-cyan-300">www.aboutads.info</a>.</li>
            </ul>
          </div>

          {/* Section 3 */}
          <div className="bg-[#111827] rounded-3xl p-6 sm:p-10 border border-white/5 shadow-xl">
            <div className="flex items-center gap-4 mb-4">
              <div className="p-3 bg-purple-500/10 rounded-2xl text-purple-400">
                <CheckCircle2 size={24} />
              </div>
              <h2 className="text-xl sm:text-2xl font-bold text-white">3. Strict Ad Quality & User Experience Standards</h2>
            </div>
            <p className="text-gray-300 leading-relaxed text-sm sm:text-base mb-4">
              We strictly enforce Google Publisher Policies and user experience guidelines. We do NOT allow:
            </p>
            <div className="grid sm:grid-cols-2 gap-4 text-sm text-gray-300">
              <div className="p-4 bg-white/5 rounded-2xl border border-white/5">
                <strong className="text-white block mb-1">🚫 No Pop-ups or Redirects</strong>
                We never use forced redirects, intrusive pop-under windows, or deceptive click traps.
              </div>
              <div className="p-4 bg-white/5 rounded-2xl border border-white/5">
                <strong className="text-white block mb-1">🚫 No Fake Buttons</strong>
                Ads are visually demarcated from game controls so you will never be tricked into clicking an ad.
              </div>
              <div className="p-4 bg-white/5 rounded-2xl border border-white/5">
                <strong className="text-white block mb-1">🚫 No Deceptive Placement</strong>
                Ads are positioned with sufficient margin away from interactive game canvases.
              </div>
              <div className="p-4 bg-white/5 rounded-2xl border border-white/5">
                <strong className="text-white block mb-1">🚫 No Malicious Code</strong>
                All ad code runs inside secure browser sandboxes with no executable downloads permitted.
              </div>
            </div>
          </div>

          {/* Section 4 */}
          <div className="bg-[#111827] rounded-3xl p-6 sm:p-10 border border-white/5 shadow-xl">
            <div className="flex items-center gap-4 mb-4">
              <div className="p-3 bg-green-500/10 rounded-2xl text-green-400">
                <FileText size={24} />
              </div>
              <h2 className="text-xl sm:text-2xl font-bold text-white">4. Editorial Independence</h2>
            </div>
            <p className="text-gray-300 leading-relaxed text-sm sm:text-base mb-4">
              Our game recommendations, ratings, blog articles, tutorials, and strategy guides are written independently by our team. Advertisers have zero influence over our game reviews, rating scores, or editorial commentary.
            </p>
            <p className="text-gray-300 leading-relaxed text-sm sm:text-base">
              We only feature games that meet our standards for safety, performance, and gameplay enjoyment.
            </p>
          </div>

          {/* Contact box */}
          <div className="text-center bg-gradient-to-r from-blue-900/30 to-purple-900/30 p-8 sm:p-10 rounded-3xl border border-white/10 shadow-2xl">
            <h3 className="text-2xl font-bold text-white mb-2">Questions About Our Ad Policies?</h3>
            <p className="text-gray-400 text-sm sm:text-base mb-6 max-w-2xl mx-auto">
              If you have any questions or encounter an inappropriate advertisement, please let us know immediately so our team can review and block it.
            </p>
            <a
              href="mailto:support@gamezonehub.in"
              className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-blue-600 to-cyan-500 hover:from-blue-500 hover:to-cyan-400 text-white font-bold rounded-xl shadow-lg transition transform hover:scale-105"
            >
              Contact Support Team
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdvertisingDisclosure;
